import React, { createContext, useContext, useState } from 'react'
import { ENV } from '../config/env';

interface AuthContextType {
  isAuthenticated: boolean
  token: string | null
  login: (token: string) => void
  logout: () => void
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    // Initialize token from localStorage
    return localStorage.getItem('auth_token');
  });

  const isAuthenticated = Boolean(token);

  const login = (newToken: string) => {
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
  };

  const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> | undefined),
      Authorization: token ? `Token ${token}` : '',
    }
    if (!isFormData) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(`${ENV.API_URL}${url}`, {
      ...options,
      headers,
    })

    if (response.status === 401) {
      logout();
    }

    return response;
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, token, login, logout, fetchWithAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 